/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { scorePoint as scorePointAPI, getMatchScore, startMatchScoring } from '../../../../services/api/scoringService';
import matchService from '../../../../services/api/matchService';
import { useWebSocket } from '../../../../contexts/WebSocketContext';
import './BadmintonScoreTracker.css';
import { getBadmintonFormat, checkRallyGameWinner, NO_CAP } from '../../../../utils/sportRules';

const BadmintonScoreTracker = ({ match: propMatch, onScoreUpdate, initialGameData }) => {
  const { matchId: urlMatchId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const webSocket = useWebSocket();
  const [match, setMatch] = useState(propMatch);
  const [loading, setLoading] = useState(!propMatch);
  const [error, setError] = useState(null);
  const [currentGame, setCurrentGame] = useState(1);
  const [gameScores, setGameScores] = useState([
    { game: 1, score1: 0, score2: 0, complete: false, winner: null },
    { game: 2, score1: 0, score2: 0, complete: false, winner: null },
    { game: 3, score1: 0, score2: 0, complete: false, winner: null }
  ]);
  const [gamesWon, setGamesWon] = useState({ player1: 0, player2: 0 });
  const [currentServer, setCurrentServer] = useState(1); // 1 or 2
  const [servingSide, setServingSide] = useState('right');
  const [intervalActive, setIntervalActive] = useState(false);
  const [intervalTimeRemaining, setIntervalTimeRemaining] = useState(0);
  const [matchComplete, setMatchComplete] = useState(false);
  const [matchWinner, setMatchWinner] = useState(null);
  const [rallyTime, setRallyTime] = useState(0);
  const [rallyActive, setRallyActive] = useState(false);
  const [matchStarted, setMatchStarted] = useState(false);

  // Match format chosen at creation (match_config.scoring_rules / game_rules),
  // BWF defaults: best of 3 games to 21, win by 2, capped at 30.
  const { ptsToWin: POINTS_TO_WIN, winMargin: WIN_MARGIN, maxPoints, totalGames: TOTAL_GAMES } = getBadmintonFormat(match);
  const MAX_POINTS_DEUCE = maxPoints && maxPoints < NO_CAP ? maxPoints : null;
  const DEUCE_POINT = POINTS_TO_WIN - 1;
  const INTERVAL_BETWEEN_GAMES = 120; // seconds

  // Fetch match data if coming from URL (not passed as prop)
  useEffect(() => {
    const fetchMatch = async () => {
      if (propMatch) {
        setMatch(propMatch);
        setLoading(false);
        return;
      }

      if (!urlMatchId) {
        setError('No match ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        console.log('🔍 Fetching match data for ID:', urlMatchId);
        
        const response = await matchService.getMatchById(urlMatchId);
        console.log('📦 Match data response:', response);
        
        if (response.success) {
          setMatch(response.data);
          console.log('✅ Match loaded successfully:', response.data);
        } else {
          setError(response.message || 'Failed to load match');
          console.error('❌ Match load failed:', response.message);
        }
      } catch (err) {
        console.error('❌ Error fetching match:', err);
        setError(err.message || 'Failed to load match data');
      } finally {
        setLoading(false);
      }
    };

    fetchMatch();
  }, [urlMatchId, propMatch]);

  // Rally timer
  useEffect(() => {
    let interval;
    if (rallyActive) {
      interval = setInterval(() => {
        setRallyTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [rallyActive]);

  // Interval timer
  useEffect(() => {
    let interval;
    if (intervalActive && intervalTimeRemaining > 0) {
      interval = setInterval(() => {
        setIntervalTimeRemaining(prev => {
          if (prev <= 1) {
            setIntervalActive(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [intervalActive, intervalTimeRemaining]);

  // WebSocket integration - Join match room and listen for score updates
  useEffect(() => {
    if (!match?.id || !webSocket) return;

    console.log('🏸 Joining badminton match room:', match.id);
    
    // Join the match room
    webSocket.joinMatch(match.id);

    // Listen for live score updates
    const removeListener = webSocket.addEventListener('live_score_update', (data) => {
      console.log('📡 Received live score update:', data);
      
      // Only process updates for this match
      if (parseInt(data.matchId) !== parseInt(match.id)) return;

      // Update current game scores
      if (data.currentGame) {
        const game = data.currentGame;
        const updatedScores = [...gameScores];
        updatedScores[game.game_number - 1] = {
          game: game.game_number,
          score1: game.participant_1_score,
          score2: game.participant_2_score,
          complete: game.status === 'completed',
          winner: game.winner_id
        };
        setGameScores(updatedScores);
      }

      // Update games won
      if (data.gamesWon) {
        setGamesWon(data.gamesWon);
      }

      // Update match completion
      if (data.matchComplete) {
        setMatchComplete(true);
        setMatchWinner(data.winner);
      }
    });

    // Cleanup on unmount
    return () => {
      console.log('🏸 Leaving badminton match room:', match.id);
      webSocket.leaveMatch(match.id);
      removeListener();
    };
  }, [match?.id, webSocket]);

  // Update serving side based on server's score
  useEffect(() => {
    const currentGameIndex = currentGame - 1;
    const serverScore = currentServer === 1 
      ? gameScores[currentGameIndex].score1 
      : gameScores[currentGameIndex].score2;
    
    // Right when even, left when odd
    setServingSide(serverScore % 2 === 0 ? 'right' : 'left');
  }, [gameScores, currentServer, currentGame]);

  const handleStartMatch = async () => {
    if (!match?.id) {
      setError('No match to start');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log('🏸 Starting match scoring for match:', match.id);
      const response = await startMatchScoring(match.id);

      if (response.success) {
        console.log('✅ Match started successfully:', response.data);
        setMatchStarted(true);
        
        // Load initial score state
        const scoreResponse = await getMatchScore(match.id);
        if (scoreResponse.success && scoreResponse.data.games.length > 0) {
          const currentGameData = scoreResponse.data.games.find(g => g.status === 'in_progress');
          if (currentGameData) {
            setCurrentGame(currentGameData.game_number);
          }
        }
      } else {
        setError(response.message || 'Failed to start match');
      }
    } catch (err) {
      console.error('Start match error:', err);
      setError(err.message || 'Failed to start match');
    } finally {
      setLoading(false);
    }
  };

  const addPoint = async (player) => {
    if (!matchStarted) {
      setError('Please start the match first');
      return;
    }
    
    if (matchComplete || intervalActive || loading) return;

    try {
      setLoading(true);
      setError(null);

      // Call backend API to score point
      const response = await scorePointAPI(match.id, currentGame, player);
      
      if (response.success) {
        const scoreData = response.data;
        
        // Update local state from backend response
        if (scoreData.current_game) {
          const game = scoreData.current_game;
          const currentGameIndex = currentGame - 1;
          const updatedScores = [...gameScores];
          updatedScores[currentGameIndex] = {
            game: currentGame,
            score1: game.participant_1_score,
            score2: game.participant_2_score,
            complete: game.status === 'completed',
            winner: game.winner_id === match.participant1_id ? 1 : (game.winner_id === match.participant2_id ? 2 : null)
          };
          setGameScores(updatedScores);
          
          // Update server and serving side from backend
          if (game.game_data) {
            setCurrentServer(game.game_data.server);
            setServingSide(game.game_data.serving_side);
          }
        }
        
        // Update games won
        setGamesWon(scoreData.games_won || gamesWon);
        
        // Check if match is complete
        if (scoreData.match_complete) {
          setMatchComplete(true);
          setMatchWinner(scoreData.winner_id === match.participant1_id ? 1 : 2);
          
          if (onScoreUpdate) {
            onScoreUpdate({
              matchId: match.id,
              matchComplete: true,
              winner: scoreData.winner_id,
              finalScore: scoreData.final_score,
              gamesWon: scoreData.games_won
            });
          }
        } else {
          // Check if we moved to next game
          const completedGames = scoreData.games.filter(g => g.status === 'completed');
          if (completedGames.length > currentGame - 1) {
            // Game just completed, start interval
            setIntervalActive(true);
            setIntervalTimeRemaining(INTERVAL_BETWEEN_GAMES);
            setCurrentGame(completedGames.length + 1);
          }
          
          if (onScoreUpdate) {
            onScoreUpdate({
              matchId: match.id,
              currentGame,
              gameScores: scoreData.games,
              gamesWon: scoreData.games_won
            });
          }
        }
        
        // Stop rally timer
        setRallyActive(false);
        setRallyTime(0);
      }
    } catch (err) {
      console.error('Score point error:', err);
      setError(err.message || 'Failed to score point');
    } finally {
      setLoading(false);
    }
  };

  const isGameComplete = (score1, score2) => {
    const winner = checkRallyGameWinner(score1, score2, POINTS_TO_WIN, WIN_MARGIN, MAX_POINTS_DEUCE);
    if (!winner) return { complete: false };
    const atCap = MAX_POINTS_DEUCE && Math.max(score1, score2) >= MAX_POINTS_DEUCE;
    return {
      complete: true,
      winner: winner === 'p1' ? 1 : 2,
      type: atCap ? 'max_score_deuce' : Math.min(score1, score2) >= DEUCE_POINT ? 'deuce_win' : 'normal_win'
    };
  };

  const isDeuce = (score1, score2) => {
    return score1 >= DEUCE_POINT && score2 >= DEUCE_POINT && !isGameComplete(score1, score2).complete;
  };

  const startRally = () => {
    setRallyActive(true);
    setRallyTime(0);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const currentGameData = gameScores[currentGame - 1];
  const isDeuceNow = isDeuce(currentGameData.score1, currentGameData.score2);

  // Show loading state
  if (loading && !match) {
    return (
      <div className="badminton-score-tracker loading">
        <div className="loading-message">
          <div className="spinner"></div>
          <p>Loading match data...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error && !match) {
    return (
      <div className="badminton-score-tracker error">
        <div className="error-message">
          <h3>⚠️ Error</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // Show no match state
  if (!match) {
    return (
      <div className="badminton-score-tracker error">
        <div className="error-message">
          <h3>⚠️ No Match Found</h3>
          <p>Unable to load match data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="badminton-score-tracker">
      <div className="match-header">
        <button 
          className="back-button"
          onClick={() => location.state?.from ? navigate(location.state.from) : navigate(-1)}
        >
          ← Back
        </button>
        <h2>Badminton Match</h2>
        <div className="match-format">{TOTAL_GAMES === 1 ? 'Single Game' : `Best of ${TOTAL_GAMES} Games`} to {POINTS_TO_WIN}</div>
      </div>

      {/* Error display */}
      {error && (
        <div className="error-banner">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {/* Start Match Button */}
      {!matchStarted && (
        <div className="start-match-section">
          <button 
            onClick={handleStartMatch}
            className="start-match-btn"
            disabled={loading}
          >
            {loading ? 'Starting Match...' : '▶️ Start Match Scoring'}
          </button>
          <p className="start-hint">Click to begin live scoring</p>
        </div>
      )}

      {/* Match Score Summary */}
      <div className="match-score-summary">
        <div className="player-section">
          <h3>{match.participant1?.name || 'Player 1'}</h3>
          <div className="games-won-badge">{gamesWon.player1} Games</div>
        </div>
        <div className="vs-divider">VS</div>
        <div className="player-section">
          <h3>{match.participant2?.name || 'Player 2'}</h3>
          <div className="games-won-badge">{gamesWon.player2} Games</div>
        </div>
      </div>

      {/* Game-by-Game Scores */}
      <div className="game-scores-grid">
        {gameScores.map((game, index) => (
          <div 
            key={game.game} 
            className={`game-score-card ${currentGame === game.game ? 'current' : ''} ${game.complete ? 'complete' : ''}`}
          >
            <div className="game-label">Game {game.game}</div>
            <div className="game-score">
              <span className={game.winner === 1 ? 'winner' : ''}>{game.score1}</span>
              <span className="score-separator">-</span>
              <span className={game.winner === 2 ? 'winner' : ''}>{game.score2}</span>
            </div>
            {game.complete && (
              <div className="game-winner">
                ✓ {game.winner === 1 ? match.participant1?.name || 'Player 1' : match.participant2?.name || 'Player 2'}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Current Game Live Score */}
      {!matchComplete && (
        <div className="current-game-tracker">
          <div className="game-title">
            Game {currentGame} {isDeuceNow && <span className="deuce-indicator">DEUCE</span>}
          </div>
          
          <div className="live-score-display">
            <div className={`player-score ${currentServer === 1 ? 'serving' : ''}`}>
              <div className="player-name">
                {match.participant1?.name || 'Player 1'}
                {currentServer === 1 && <span className="server-badge">⚡ Serving</span>}
              </div>
              <div className="score-value">{currentGameData.score1}</div>
              <button 
                className="add-point-btn"
                onClick={() => addPoint(1)}
                disabled={intervalActive || matchComplete}
              >
                +1 Point
              </button>
            </div>

            <div className="score-divider">
              <div className="serving-side-indicator">
                <div className="court-diagram">
                  <div className={`service-box left ${servingSide === 'left' ? 'active' : ''}`}>L</div>
                  <div className={`service-box right ${servingSide === 'right' ? 'active' : ''}`}>R</div>
                </div>
                <div className="serving-instruction">
                  Serve from {servingSide.toUpperCase()} court
                </div>
              </div>
            </div>

            <div className={`player-score ${currentServer === 2 ? 'serving' : ''}`}>
              <div className="player-name">
                {match.participant2?.name || 'Player 2'}
                {currentServer === 2 && <span className="server-badge">⚡ Serving</span>}
              </div>
              <div className="score-value">{currentGameData.score2}</div>
              <button 
                className="add-point-btn"
                onClick={() => addPoint(2)}
                disabled={intervalActive || matchComplete}
              >
                +1 Point
              </button>
            </div>
          </div>

          {/* Rally Timer */}
          <div className="rally-timer">
            <div className="timer-display">{formatTime(rallyTime)}</div>
            <button 
              className={`rally-btn ${rallyActive ? 'active' : ''}`}
              onClick={() => rallyActive ? setRallyActive(false) : startRally()}
            >
              {rallyActive ? 'Stop Rally' : 'Start Rally'}
            </button>
          </div>

          {/* Interval Timer */}
          {intervalActive && (
            <div className="interval-overlay">
              <div className="interval-content">
                <h3>Interval</h3>
                <div className="interval-timer">{formatTime(intervalTimeRemaining)}</div>
                <p>
                  {intervalTimeRemaining > 90 ? 'Between Games' : '11-Point Interval'}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Match Complete */}
      {matchComplete && (
        <div className="match-complete">
          <h2>Match Complete!</h2>
          <div className="winner-announcement">
            🏆 {matchWinner === 1 ? match.participant1?.name || 'Player 1' : match.participant2?.name || 'Player 2'} Wins!
          </div>
          <div className="final-score">
            Final Score: {gamesWon.player1} - {gamesWon.player2}
          </div>
        </div>
      )}

      {/* BWF Rules Reference */}
      <div className="rules-reference">
        <details>
          <summary>BWF Rules Quick Reference</summary>
          <ul>
            <li>Rally scoring to {POINTS_TO_WIN} points</li>
            <li>Deuce at {DEUCE_POINT}-{DEUCE_POINT} (win by {WIN_MARGIN}{MAX_POINTS_DEUCE ? ` or first to ${MAX_POINTS_DEUCE}` : ''})</li>
            <li>Best of 3 games format</li>
            <li>Serve from RIGHT when server's score is EVEN</li>
            <li>Serve from LEFT when server's score is ODD</li>
            <li>60-second interval at 11 points</li>
            <li>120-second interval between games</li>
          </ul>
        </details>
      </div>
    </div>
  );
};

export default BadmintonScoreTracker;
